import { useCallback, useState } from "react"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"
import type { UpdateForFinancePaymentRequest } from "../types"

const BASE_URL = `${SMOOTH_LINE_API_URL}/base_api/smoother/screen`

export const useForFinancePaymentMutations = () => {
  const { patchWithToken, deleteWithToken, postWithToken } = useSmoothApi()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const run = useCallback(async <T>(request: () => Promise<T>): Promise<T> => {
    setIsSubmitting(true)
    try {
      return await request()
    } finally {
      setIsSubmitting(false)
    }
  }, [])

  const updatePayment = useCallback(
    (id: number, params: UpdateForFinancePaymentRequest) =>
      run(() => patchWithToken(`${BASE_URL}/for_finance_payments/${id}`, params)),
    [patchWithToken, run]
  )

  const deletePayment = useCallback(
    (id: number) => run(() => deleteWithToken(`${BASE_URL}/for_finance_payments/${id}`)),
    [deleteWithToken, run]
  )

  // スプレッドシートの 1 行（タブ区切り）を取り込む
  const importRow = useCallback(
    async (rowData: string) => {
      const response = await run(() =>
        postWithToken(`${BASE_URL}/for_finance_payments/row_import`, { rowData })
      )
      return response.data as { id: number }
    },
    [postWithToken, run]
  )

  const createTransferApplication = useCallback(
    async (paymentIds: number[]) => {
      const response = await run(() =>
        postWithToken(`${BASE_URL}/transfer_applications`, { paymentIds })
      )
      return response.data as { id: number }
    },
    [postWithToken, run]
  )

  return { updatePayment, deletePayment, importRow, createTransferApplication, isSubmitting }
}
